import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';

import AssignDeviceModal from '@/components/Devices/AssignDeviceModal';
import DeviceDetailsModal from '@/components/Devices/DeviceDetailsModal';
import ExtendRentalModal from '@/components/Devices/ExtendRentalModal';
import MaintenanceModal from '@/components/Devices/MaintenanceModal';
import MarkUnfitModal from '@/components/Devices/MarkUnfitModal';
import type { DeviceAction } from '@/components/Devices/deviceActionTypes';
import { deviceService } from '@/feature/device/services/deviceService';
import type { IDeviceListItem } from '@/feature/device/types/device';
import { getDeviceLookupId } from '@/feature/device/utils/deviceLabels';
import ConfirmModal from '@/shared/components/ui/Modals/ConfirmModal';

export function useDeviceModals(onRefresh: () => Promise<void> | void) {
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [detailsRefreshKey, setDetailsRefreshKey] = useState(0);
  const [assignDevice, setAssignDevice] = useState<IDeviceListItem | null>(null);
  const [extendDevice, setExtendDevice] = useState<IDeviceListItem | null>(null);
  const [maintenanceDevice, setMaintenanceDevice] = useState<IDeviceListItem | null>(null);
  const [unfitDevice, setUnfitDevice] = useState<IDeviceListItem | null>(null);
  const [releaseDevice, setReleaseDevice] = useState<IDeviceListItem | null>(null);
  const [completeDevice, setCompleteDevice] = useState<IDeviceListItem | null>(null);

  const refreshAll = useCallback(async () => {
    await onRefresh();
    setDetailsRefreshKey((key) => key + 1);
  }, [onRefresh]);

  const openDetails = useCallback((device: IDeviceListItem) => {
    setDetailsId(getDeviceLookupId(device));
  }, []);

  const handleAction = useCallback((action: DeviceAction, device: IDeviceListItem) => {
    switch (action) {
      case 'view':
        setDetailsId(getDeviceLookupId(device));
        break;
      case 'assign':
        setAssignDevice(device);
        break;
      case 'extend':
        setExtendDevice(device);
        break;
      case 'release':
        setReleaseDevice(device);
        break;
      case 'maintenance':
        setMaintenanceDevice(device);
        break;
      case 'complete-maintenance':
        setCompleteDevice(device);
        break;
      case 'unfit':
        setUnfitDevice(device);
        break;
      default:
        break;
    }
  }, []);

  const getReleaseConfirmMessage = () => {
    if (!releaseDevice) {
      return '';
    }
    const orgName = releaseDevice.rented_to?.org_name || 'an organization';
    if (releaseDevice.device_status?.toLowerCase() === 'maintenance') {
      return `${releaseDevice.device_name} is reserved for ${orgName}. Releasing will remove the organization assignment; the device will remain in maintenance until maintenance is completed.`;
    }
    return `${releaseDevice.device_name} is currently rented to ${orgName}. Are you sure you want to release this device?`;
  };

  const modalElements = (
    <>
      <DeviceDetailsModal
        isOpen={detailsId !== null}
        deviceId={detailsId}
        refreshKey={detailsRefreshKey}
        onClose={() => setDetailsId(null)}
        onAction={handleAction}
      />
      <AssignDeviceModal
        isOpen={assignDevice !== null}
        deviceName={assignDevice?.device_name || ''}
        onClose={() => setAssignDevice(null)}
        onAssign={async (data) => {
          if (!assignDevice) {
            return false;
          }
          const result = await deviceService.rentDevice(getDeviceLookupId(assignDevice), {
            org_id: data.org_id,
            project_id: data.project_id,
            rental_days: data.rental_days,
            rental_hours: data.rental_hours,
            billing_cycle: 'hourly',
            notes: data.notes,
          });
          if (result) {
            toast.success(result.message || 'Device assigned successfully.');
            await refreshAll();
            return true;
          }
          return false;
        }}
      />
      <ExtendRentalModal
        isOpen={extendDevice !== null}
        deviceName={extendDevice?.device_name || ''}
        orgName={extendDevice?.rented_to?.org_name}
        rentalStart={extendDevice?.rental_start_at}
        rentalEnd={extendDevice?.rental_end_at}
        onClose={() => setExtendDevice(null)}
        onExtend={async ({ extension_days, extension_hours }) => {
          if (!extendDevice) {
            return false;
          }
          const result = await deviceService.extendRental(getDeviceLookupId(extendDevice), {
            extension_days,
            extension_hours,
          });
          if (result) {
            toast.success(result.message || 'Device rental extended successfully.');
            await refreshAll();
            return true;
          }
          return false;
        }}
      />
      <MaintenanceModal
        isOpen={maintenanceDevice !== null}
        deviceName={maintenanceDevice?.device_name || ''}
        orgName={maintenanceDevice?.rented_to?.org_name}
        onClose={() => setMaintenanceDevice(null)}
        onSubmit={async (notes) => {
          if (!maintenanceDevice) {
            return false;
          }
          const result = await deviceService.putInMaintenance(
            getDeviceLookupId(maintenanceDevice),
            notes
          );
          if (result) {
            toast.success(result.message || 'Device moved to maintenance.');
            await refreshAll();
            return true;
          }
          return false;
        }}
      />
      <MarkUnfitModal
        isOpen={unfitDevice !== null}
        deviceName={unfitDevice?.device_name || ''}
        orgName={unfitDevice?.rented_to?.org_name}
        status={unfitDevice?.device_status}
        onClose={() => setUnfitDevice(null)}
        onSubmit={async (reason) => {
          if (!unfitDevice) {
            return false;
          }
          const result = await deviceService.markUnfit(getDeviceLookupId(unfitDevice), reason);
          if (result) {
            toast.success(result.message || 'Device marked as Unfit.');
            await refreshAll();
            setDetailsId(null);
            return true;
          }
          return false;
        }}
      />
      <ConfirmModal
        isOpen={releaseDevice !== null}
        onClose={() => setReleaseDevice(null)}
        title='Release Device?'
        variant='danger'
        confirmText='Release Device'
        message={getReleaseConfirmMessage()}
        onConfirm={async () => {
          if (!releaseDevice) {
            return;
          }
          const releasingFromMaintenance =
            releaseDevice.device_status?.toLowerCase() === 'maintenance';
          const result = await deviceService.releaseDevice(getDeviceLookupId(releaseDevice));
          if (result) {
            toast.success(
              result.message ||
                (releasingFromMaintenance
                  ? 'Device released from organization. It remains in maintenance.'
                  : 'Device released successfully.')
            );
            await refreshAll();
          }
        }}
      />
      <ConfirmModal
        isOpen={completeDevice !== null}
        onClose={() => setCompleteDevice(null)}
        title='Complete Maintenance?'
        variant='info'
        confirmText='Complete Maintenance'
        message={
          completeDevice
            ? `${completeDevice.device_name} will return to ${
                completeDevice.rented_to ? 'Rented' : 'Available'
              } status.`
            : ''
        }
        onConfirm={async () => {
          if (!completeDevice) {
            return;
          }
          const result = await deviceService.completeMaintenance(getDeviceLookupId(completeDevice));
          if (result) {
            toast.success(result.message || 'Maintenance completed.');
            await refreshAll();
          }
        }}
      />
    </>
  );

  return { openDetails, handleAction, modalElements };
}
